import { getCellColour } from 'components/spreadsheet/helpers/colouring'
import { getRowValue } from 'components/spreadsheet/helpers/functions'
import { ITableColumn, ITableViewColumn, ITableRow, ITableViewSort, IChartSort, ITableViewColour } from 'types'
import { tint, contrastText } from 'helpers/colour'
import { CompressedColumnStore } from 'components/spreadsheet/helpers/compressed-column-store'
import { IColumnValuesCount } from 'components/spreadsheet/types'
import {
  addThousandSeparator,
  neatRound,
  numberWithCommas,
  formatPercentage,
  formatDate,
  formatDateTime
} from 'components/spreadsheet/helpers/format'

// ──────────────── Constants ────────────────
export const MULTISELECT_COLUMN_KINDS = ['multiselect', 'multilink']
export const NUMERIC_COLUMN_KINDS = ['integer', 'float', 'percentage', 'duration']
export const MAX_BAR_THICKNESS = 150
export const OTHER_LABEL = '\u200BOther'

// ──────────────── Types ────────────────
export type AggMode = 'count' | 'sum' | 'avg' | 'earliest' | 'latest'

export interface AggEntry {
  sum: number // running total
  count: number // how many rows contributed
}

export interface StackAggEntry {
  sum: number // running total for this label in this stack
  count: number // how many rows contributed
}

export type Agg = {
  [stackCat: string]: {
    // e.g. "In Progress", "Done", …
    [label: string]: StackAggEntry // e.g. "Week 1", "2025-05-29", …
  }
}

// ──────────────── Pure helpers ────────────────

export const isAggMode = (v: unknown): v is AggMode =>
  typeof v === 'string' && (v === 'count' || v === 'sum' || v === 'avg' || v === 'earliest' || v === 'latest')

// helper to get YYYY-MM key from a Date
export const monthKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`

// Centralised helper for inclusion & numeric conversion
export const evaluateAggregatedValue = (raw: unknown, mode: AggMode) => {
  const isEmpty = raw === null || raw === undefined || raw === ''
  if (mode === 'count') return { include: !isEmpty, value: 1 }

  const numeric = typeof raw === 'number' ? raw : Number(raw)
  const isNumeric = !Number.isNaN(numeric) && !isEmpty
  return { include: isNumeric, value: isNumeric ? numeric : 0 }
}

// pick the number you actually want to chart
export const valueFor = (entry: AggEntry | StackAggEntry, agg: AggMode = 'count') => {
  switch (agg) {
    case 'avg':
      return entry.count ? entry.sum / entry.count : 0
    case 'sum':
      return entry.sum
    case 'count':
    default:
      return entry.count
  }
}

export const buildToday = () => {
  const date = new Date()
  date.setHours(0, 0, 0, 0)
  return date
}

export const getLabels = (
  column: ITableColumn,
  row: ITableRow,
  columnIdToIndex: Map<string, number>,
  columnStore?: CompressedColumnStore | null
) => {
  const value = getRowValue(row, column.publicId, columnIdToIndex, columnStore)
  if (value === null || value === undefined || value === '') {
    return column.kind === 'checkbox' ? ['false'] : ['']
  }
  // For multiselect columns, assume value is an array of selections
  if (MULTISELECT_COLUMN_KINDS.includes(column.kind) && Array.isArray(value)) {
    return value.map(String)
  }
  // Add any additional formatting for timestamps or JSON as needed.
  return [String(value)]
}

export const seriesColour = (idx: number, primaryColour: string) => {
  const palette = [
    primaryColour,
    tint(primaryColour, 0.05),
    tint(primaryColour, 0.1),
    tint(primaryColour, 0.15),
    tint(primaryColour, 0.2),
    tint(primaryColour, 0.25),
    tint(primaryColour, 0.3),
    tint(primaryColour, 0.35),
    tint(primaryColour, 0.4),
    tint(primaryColour, 0.45),
    tint(primaryColour, 0.5)
  ]
  return palette[idx % palette.length]
}

// helper to get a label colour (by label, not value)
export const colourForLabel = (
  lbl: string,
  colourCache: Map<string, string>,
  labelColumn: ITableViewColumn,
  columnValuesCount: IColumnValuesCount,
  colourSettings: ITableViewColour[] | undefined,
  primaryColour: string
) => {
  const hit = colourCache.get(lbl)
  if (hit) return hit
  const { backgroundColour } = getCellColour(
    labelColumn,
    columnValuesCount,
    null,
    lbl,
    colourSettings,
    false,
    primaryColour
  )
  colourCache.set(lbl, backgroundColour)
  return backgroundColour
}

export const formatColumnValue = (value: number | string, column: ITableColumn, aggregate: string): string => {
  // Date aggregations return a date string
  if (aggregate === 'earliest' || aggregate === 'latest') {
    if (!value || typeof value !== 'string') return '—'
    if (column.kind === 'datetime') return formatDateTime(value, column.dateFormat || 'dd/MM/yyyy HH:mm')
    return formatDate(value, column.dateFormat || 'dd/MM/yyyy')
  }
  // Count aggregate is a row count, not a value in the column's units, so it is
  // always a plain integer regardless of the column's kind or decimal places.
  if (aggregate === 'count') {
    return numberWithCommas(Math.round(value as number))
  }
  const num = value as number
  // Columns default to the separator being on, matching the column model default.
  const separator = column.thousandSeparator ?? true
  switch (column.kind) {
    case 'integer': {
      const rounded = Math.round(num).toString()
      return separator ? addThousandSeparator(rounded) : rounded
    }
    case 'float': {
      // Group the fixed string rather than the parsed number so trailing zeros survive:
      // 12345.6 at 2 dp is "12,345.60", not "12,345.6".
      const formatted = num.toFixed(column.decimalPlaces ?? 2)
      return separator ? addThousandSeparator(formatted) : formatted
    }
    case 'percentage':
      // Values stored as decimals (0.62 = 62%), multiply by 100 for display
      return formatPercentage(num * 100, column.decimalPlaces ?? 0)
    case 'duration': {
      const s = Math.abs(Math.round(num))
      const hours = Math.floor(s / 3600)
      const minutes = Math.floor((s % 3600) / 60)
      // The grid groups the hours too, so a 1000-hour duration reads "1,000:00" in both
      // places rather than "1,000:00" in a cell and "1000:00" on a label.
      const paddedHours = hours.toString().padStart(2, '0')
      const hoursText = separator ? addThousandSeparator(paddedHours) : paddedHours
      return `${num < 0 ? '-' : ''}${hoursText}:${minutes.toString().padStart(2, '0')}`
    }
    default:
      return Number.isInteger(num) ? numberWithCommas(num) : numberWithCommas(neatRound(num))
  }
}

// ──────────────── Build functions ────────────────

export const buildLabelsByRow = (
  labelColumn: ITableViewColumn | null | undefined,
  rows: ITableRow[],
  columnIdToIndex: Map<string, number>,
  columnStore?: CompressedColumnStore | null
) => {
  if (!labelColumn || labelColumn === null) return new Map<string, string[]>()
  const map = new Map<string, string[]>()
  rows.forEach((row) => {
    map.set(row.publicId, getLabels(labelColumn, row, columnIdToIndex, columnStore))
  })
  return map
}

export const buildAllLabelsFromLabelColumn = (labelsByRow: Map<string, string[]>) => {
  const s = new Set<string>()
  labelsByRow.forEach((lbls) => lbls.forEach((l) => s.add(l)))
  return Array.from(s)
}

export const buildSCurveChartData = (
  chartType: string | null,
  date1Column: ITableViewColumn | null | undefined,
  date2Column: ITableViewColumn | null | undefined,
  rows: ITableRow[],
  columnIdToIndex: Map<string, number>,
  columnStore?: CompressedColumnStore | null
) => {
  if (chartType !== 's-curve' || !date1Column) return null

  interface Count {
    [isoMonth: string]: number
  }
  const monthCounts1: Count = {}
  const monthCounts2: Count = {}

  let minDate: Date | null = null
  let maxDate: Date | null = null

  rows.forEach((row) => {
    const raw1 = getRowValue(row, date1Column.publicId, columnIdToIndex, columnStore)
    if (raw1) {
      const d1 = new Date(String(raw1))
      if (!isNaN(d1.getTime())) {
        const key = monthKey(d1)
        monthCounts1[key] = (monthCounts1[key] ?? 0) + 1
        minDate = !minDate || d1 < minDate ? d1 : minDate
        maxDate = !maxDate || d1 > maxDate ? d1 : maxDate
      }
    }

    if (date2Column) {
      const raw2 = getRowValue(row, date2Column.publicId, columnIdToIndex, columnStore)
      if (raw2) {
        const d2 = new Date(String(raw2))
        if (!isNaN(d2.getTime())) {
          const key = monthKey(d2)
          monthCounts2[key] = (monthCounts2[key] ?? 0) + 1
          minDate = !minDate || d2 < minDate ? d2 : minDate
          maxDate = !maxDate || d2 > maxDate ? d2 : maxDate
        }
      }
    }
  })

  // Build ordered list of months between min & max (inclusive)
  const labels: string[] = []
  const counts1: number[] = []
  const counts2: number[] = []

  const runCumulative1: number[] = []
  const runCumulative2: number[] = []

  let cum1 = 0
  let cum2 = 0

  if (!minDate || !maxDate) return null

  const _min = minDate as Date
  const _max = maxDate as Date

  const cursor = new Date(_min.getFullYear(), _min.getMonth(), 1)
  const end = new Date(_max.getFullYear(), _max.getMonth(), 1)

  while (cursor <= end) {
    const key = monthKey(cursor)
    const label = cursor.toLocaleString('default', { month: 'short', year: 'numeric' })
    labels.push(label)

    const c1 = monthCounts1[key] ?? 0
    const c2 = monthCounts2[key] ?? 0

    counts1.push(c1)
    counts2.push(c2)

    cum1 += c1
    cum2 += c2

    runCumulative1.push(cum1)
    runCumulative2.push(cum2)

    // next month
    cursor.setMonth(cursor.getMonth() + 1)
  }

  const blue = '#1976d2'
  const green = '#2e7d32'

  const datasets: any[] = [
    { type: 'bar', label: date1Column.name, data: counts1, backgroundColor: blue, maxBarThickness: MAX_BAR_THICKNESS }
  ]

  if (date2Column) {
    datasets.push({
      type: 'bar',
      label: date2Column.name,
      data: counts2,
      backgroundColor: green,
      maxBarThickness: MAX_BAR_THICKNESS
    })
  }

  datasets.push({
    type: 'line',
    label: `${date1Column.name} Running Total`,
    data: runCumulative1,
    borderColor: blue,
    borderWidth: 2,
    tension: 0,
    fill: false,
    pointRadius: 2
  })

  if (date2Column) {
    datasets.push({
      type: 'line',
      label: `${date2Column.name} Running Total`,
      data: runCumulative2,
      borderColor: green,
      borderWidth: 2,
      tension: 0,
      fill: false,
      pointRadius: 2
    })
  }

  return { labels, datasets }
}

/**
 * Return the labels in the correct order:
 *   1. by aggregate (if enabled)
 *   2. by the user's sort on the label column
 */
export function orderLabels(
  labels: string[],
  aggregateOrder: IChartSort,
  sortSettings: ITableViewSort[],
  valueLookup: (label: string) => number,
  columns: ITableViewColumn[],
  columnLabelId: string
): string[] {
  // pre-compute the secondary (label-column) comparator
  const labelColumn = columns.find((c) => c.publicId === columnLabelId)
  const userSort = sortSettings?.find((s) => s.columnId === labelColumn?.publicId)

  const dirLabel = userSort?.direction === 'desc' ? -1 : 1
  const cmpLabel =
    labelColumn && userSort
      ? (() => {
          switch (labelColumn.kind) {
            case 'integer':
            case 'float':
            case 'percentage':
            case 'duration':
              return (a: string, b: string) => (Number(a) - Number(b)) * dirLabel
            case 'date':
            case 'datetime':
              return (a: string, b: string) => (new Date(a).getTime() - new Date(b).getTime()) * dirLabel
            default:
              return (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true }) * dirLabel
          }
        })()
      : () => 0 // no secondary sort

  // full comparator
  const cmp = (a: string, b: string) => {
    if (aggregateOrder) {
      const dirAgg = aggregateOrder === 'asc' ? 1 : -1
      const diff = (valueLookup(a) - valueLookup(b)) * dirAgg
      if (diff !== 0) return diff
    }
    // either aggregates are equal or aggregate ordering is OFF
    return cmpLabel(a, b)
  }

  return [...labels].sort(cmp) // never mutate original
}

export const buildCardData = (
  chartType: string | null,
  valueColumns: ITableViewColumn[] | null,
  rows: ITableRow[],
  columnIdToIndex: Map<string, number>,
  columnStore: CompressedColumnStore | null | undefined,
  aggregate: string | null,
  aggMode: AggMode
) => {
  if (chartType !== 'card' || !valueColumns || valueColumns.length === 0) return null

  const agg = aggregate || 'count'
  const isDateAgg = agg === 'earliest' || agg === 'latest'

  return valueColumns.map((col) => {
    if (isDateAgg) {
      // Date aggregation: find earliest or latest date by timestamp comparison
      let result: string | null = null
      let resultTs = 0
      rows.forEach((row) => {
        const raw = getRowValue(row, col.publicId, columnIdToIndex, columnStore)
        if (raw !== null && raw !== undefined && raw !== '') {
          const dateStr = String(raw)
          const ts = Date.parse(dateStr)
          if (!Number.isNaN(ts)) {
            if (result === null) {
              result = dateStr
              resultTs = ts
            } else if (agg === 'earliest' && ts < resultTs) {
              result = dateStr
              resultTs = ts
            } else if (agg === 'latest' && ts > resultTs) {
              result = dateStr
              resultTs = ts
            }
          }
        }
      })
      return { column: col, value: result ?? '' }
    }

    // Numeric aggregation: count / sum / avg
    let sum = 0
    let count = 0
    rows.forEach((row) => {
      const raw = getRowValue(row, col.publicId, columnIdToIndex, columnStore)
      const { include, value } = evaluateAggregatedValue(raw, aggMode)
      if (include) {
        sum += value
        count += 1
      }
    })
    const aggregatedValue = aggMode === 'avg' ? (count ? sum / count : 0) : aggMode === 'sum' ? sum : count
    return { column: col, value: neatRound(aggregatedValue) }
  })
}

export const buildGroupedCardData = (
  chartType: string | null,
  labelColumn: ITableViewColumn | null | undefined,
  valueColumns: ITableViewColumn[] | null,
  rows: ITableRow[],
  columnIdToIndex: Map<string, number>,
  columnStore: CompressedColumnStore | null | undefined,
  aggregate: string | null,
  aggMode: AggMode,
  allLabelsFromLabelColumn: string[],
  labelsByRow: Map<string, string[]>
) => {
  if (chartType !== 'card' || !labelColumn || !valueColumns || valueColumns.length === 0) return null

  const agg = aggregate || 'count'
  const isDateAgg = agg === 'earliest' || agg === 'latest'
  const labels = allLabelsFromLabelColumn

  // Pre-index rows by label so we don't re-filter for every label x column
  const rowsByLabel = new Map<string, typeof rows>()
  rows.forEach((row) => {
    const rowLabels = labelsByRow.get(row.publicId) ?? []
    for (const l of rowLabels) {
      const arr = rowsByLabel.get(l)
      if (arr) arr.push(row)
      else rowsByLabel.set(l, [row])
    }
  })

  // Build per-label, per-column aggregates
  const groups: { label: string; cards: { column: ITableViewColumn; value: number | string }[] }[] = []

  for (const lbl of labels) {
    const cards = valueColumns.map((col) => {
      const matchingRows = rowsByLabel.get(lbl) ?? []

      if (isDateAgg) {
        let result: string | null = null
        let resultTs = 0
        matchingRows.forEach((row) => {
          const raw = getRowValue(row, col.publicId, columnIdToIndex, columnStore)
          if (raw !== null && raw !== undefined && raw !== '') {
            const dateStr = String(raw)
            const ts = Date.parse(dateStr)
            if (!Number.isNaN(ts)) {
              if (result === null) {
                result = dateStr
                resultTs = ts
              } else if (agg === 'earliest' && ts < resultTs) {
                result = dateStr
                resultTs = ts
              } else if (agg === 'latest' && ts > resultTs) {
                result = dateStr
                resultTs = ts
              }
            }
          }
        })
        return { column: col, value: result ?? '' }
      }

      let sum = 0
      let count = 0
      matchingRows.forEach((row) => {
        const raw = getRowValue(row, col.publicId, columnIdToIndex, columnStore)
        const { include, value } = evaluateAggregatedValue(raw, aggMode)
        if (include) {
          sum += value
          count += 1
        }
      })
      const aggregatedValue = aggMode === 'avg' ? (count ? sum / count : 0) : aggMode === 'sum' ? sum : count
      return { column: col, value: neatRound(aggregatedValue) }
    })
    groups.push({ label: lbl, cards })
  }

  return groups
}

export const buildAggregatedPerSeries = (
  chartType: string | null,
  labelColumn: ITableViewColumn | null | undefined,
  valueColumns: ITableViewColumn[] | null,
  rows: ITableRow[],
  columnIdToIndex: Map<string, number>,
  columnStore: CompressedColumnStore | null | undefined,
  aggMode: AggMode,
  labelsByRow: Map<string, string[]>
): Record<string /*colId*/, Record<string /*label*/, AggEntry>> => {
  if (chartType === 'card') return {}
  if (!labelColumn || !valueColumns || valueColumns.length === 0) return {}
  const out: Record<string, Record<string, AggEntry>> = {}

  rows.forEach((row) => {
    const labels = labelsByRow.get(row.publicId) ?? []
    valueColumns.forEach((col) => {
      const raw = getRowValue(row, col.publicId, columnIdToIndex, columnStore)
      const { include, value } = evaluateAggregatedValue(raw, aggMode)
      if (!include) return

      const num = value
      if (!out[col.publicId]) out[col.publicId] = {}
      const mapForCol = out[col.publicId]
      labels.forEach((lbl) => {
        if (!mapForCol[lbl]) mapForCol[lbl] = { sum: 0, count: 0 }
        const bucket = mapForCol[lbl]
        bucket.sum += num
        bucket.count += 1
      })
    })
  })

  return out
}

export const buildFinalLabelsAndOther = (
  maxCategories: number | null | undefined,
  aggregatedLabels: string[],
  firstSeries: Record<string, AggEntry> | undefined,
  aggMode: AggMode,
  valueColumns: ITableViewColumn[] | null,
  aggregatedPerSeries: Record<string, Record<string, AggEntry>>
) => {
  const maxCat = maxCategories
  if (!maxCat || maxCat <= 0 || aggregatedLabels.length <= maxCat) {
    return { finalLabels: aggregatedLabels, otherAggregated: null }
  }

  // Sort by first series aggregate descending to determine top N
  const ranked = [...aggregatedLabels].sort((a, b) => {
    const aVal = firstSeries ? valueFor(firstSeries[a] || { sum: 0, count: 0 }, aggMode) : 0
    const bVal = firstSeries ? valueFor(firstSeries[b] || { sum: 0, count: 0 }, aggMode) : 0
    return bVal - aVal
  })

  const otherLabels = new Set(ranked.slice(maxCat))

  // Merge "Other" aggregates per series
  const otherBySeries: Record<string, AggEntry> = {}
  if (valueColumns) {
    valueColumns.forEach((col) => {
      const byLabel = aggregatedPerSeries[col.publicId] || {}
      const merged: AggEntry = { sum: 0, count: 0 }
      otherLabels.forEach((lbl) => {
        const entry = byLabel[lbl]
        if (entry) {
          merged.sum += entry.sum
          merged.count += entry.count
        }
      })
      otherBySeries[col.publicId] = merged
    })
  }

  // Re-apply the original ordering to the top labels only
  const orderedTop = aggregatedLabels.filter((l) => !otherLabels.has(l))
  return { finalLabels: [...orderedTop, OTHER_LABEL], otherAggregated: otherBySeries }
}

export const buildStandardChartData = (
  chartType: string | null,
  valueColumns: ITableViewColumn[] | null,
  finalLabels: string[],
  aggregatedPerSeries: Record<string, Record<string, AggEntry>>,
  otherAggregated: Record<string, AggEntry> | null,
  aggregate: string | null,
  isNumericAggregation: boolean,
  columnValuesCount: IColumnValuesCount,
  colourSettings: ITableViewColour[] | undefined,
  primaryColour: string,
  colourForLabelFn: (lbl: string) => string
) => {
  if (chartType === 'card' || !valueColumns || valueColumns.length === 0) {
    return { labels: finalLabels, datasets: [] }
  }

  const isLineLike = chartType === 'line' || chartType === 'area' || chartType === 'multiaxis-line'

  // Rotate point shapes so each line is visually distinct in the legend and on the chart
  const pointShapes = ['circle', 'triangle', 'rect', 'rectRot', 'star', 'cross', 'crossRot'] as const

  // Build one dataset per value column
  const datasets = valueColumns.map((col, idx) => {
    const byLabel = aggregatedPerSeries[col.publicId] || {}
    const data = finalLabels.map((lbl) => {
      if (lbl === OTHER_LABEL && otherAggregated) {
        const entry = otherAggregated[col.publicId] || { sum: 0, count: 0 }
        return valueFor(entry, (aggregate ?? 'count') as AggMode)
      }
      const entry = byLabel[lbl] || { sum: 0, count: 0 }
      if (isLineLike && entry.count === 0) return null
      return valueFor(entry, (aggregate ?? 'count') as AggMode)
    })

    // Colours: if numeric aggregation (avg/sum), colour by that column; else by label
    const colours =
      isNumericAggregation && (aggregate === 'avg' || aggregate === 'sum')
        ? data.map(
            (v) => getCellColour(col, columnValuesCount, null, v, colourSettings, false, primaryColour).backgroundColour
          )
        : finalLabels.map((lbl) => (lbl === OTHER_LABEL ? '#9E9E9E' : colourForLabelFn(lbl)))

    const baseDataset: any = {
      label: col.name,
      data,
      pointRadius: 2,
      maxBarThickness: MAX_BAR_THICKNESS,
      // Percentage columns store decimals (0.62 = 62%). When the value is aggregated in
      // the column's own units (sum/avg), the data label must render as a percentage rather
      // than the raw decimal. Stamped per-dataset so the datalabels formatter can read it
      // from ctx.dataset (survives react-chartjs-2's clone; see chart/index.tsx).
      _percentColumn: col.kind === 'percentage' && (aggregate === 'sum' || aggregate === 'avg'),
      _decimalPlaces: col.decimalPlaces ?? 0,
      // The whole column, so the datalabels formatter can apply its number formatting
      // (decimal places + thousand separator) the same way the grid does.
      _valueColumn: col
    }

    if (isLineLike) {
      // Pre-compute a "carried" colour array: carry a matched colour forward
      // only when there is another matched rule ahead. Once past the last match,
      // revert to the default colour so the line doesn't stay coloured forever.
      const defaultCol = primaryColour

      // For each index, find the next index (inclusive) that has a non-default colour
      const hasMatchAhead: boolean[] = new Array(colours.length)
      let foundAhead = false
      for (let i = colours.length - 1; i >= 0; i--) {
        if (colours[i] !== defaultCol) foundAhead = true
        hasMatchAhead[i] = foundAhead
      }

      const carried: string[] = []
      let last = defaultCol
      for (let i = 0; i < colours.length; i++) {
        if (colours[i] !== defaultCol) {
          last = colours[i]
        } else if (!hasMatchAhead[i]) {
          last = defaultCol
        }
        carried[i] = last
      }

      const shape = pointShapes[idx % pointShapes.length]

      return {
        ...baseDataset,
        backgroundColor: colours,
        borderColor: seriesColour(idx, primaryColour),
        pointBackgroundColor: colours,
        pointStyle: shape,
        segment: {
          borderColor: (ctx: any) => {
            const c = carried[ctx.p1DataIndex]
            return c !== defaultCol ? c : seriesColour(idx, primaryColour)
          }
        }
      }
    }

    return {
      ...baseDataset,
      backgroundColor: colours,
      borderColor: seriesColour(idx, primaryColour)
    }
  })

  return { labels: finalLabels, datasets }
}

export const buildGanttChartData = (
  chartType: string | null,
  labelColumn: ITableViewColumn | null | undefined,
  startColumn: ITableViewColumn | null | undefined,
  endColumn: ITableViewColumn | null | undefined,
  deadlineColumn: ITableViewColumn | null | undefined,
  rows: ITableRow[],
  columnIdToIndex: Map<string, number>,
  columnStore: CompressedColumnStore | null | undefined,
  columnValuesCount: IColumnValuesCount,
  colourSettings: ITableViewColour[] | undefined,
  primaryColour: string
) => {
  if (chartType !== 'gantt' || !labelColumn || !startColumn || !endColumn) return null

  const labels: string[] = []
  const data: any[] = []
  const colours: string[] = []
  const deadlineTimestamps: (number | null)[] = []

  // Instead of tracking dates directly, we'll track timestamps
  let minTimestamp: number | null = null
  let maxTimestamp: number | null = null

  // Process the sorted, valid rows
  rows.forEach((row) => {
    const rawStart = getRowValue(row, startColumn.publicId, columnIdToIndex, columnStore)
    const rawEnd = getRowValue(row, endColumn.publicId, columnIdToIndex, columnStore)
    if (!rawStart || !rawEnd) {
      return
    }

    const taskName = getLabels(labelColumn, row, columnIdToIndex, columnStore)[0]

    // Create dates and get timestamps
    const startDate = new Date(String(rawStart))
    const endDate = new Date(String(rawEnd))

    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      return
    }

    // Ensure dates right way round
    if (startDate > endDate) {
      return
    }

    const startTimestamp = startDate.getTime()
    const endTimestamp = endDate.getTime()

    const finalStartDate = new Date(startTimestamp)
    const finalEndDate = new Date(endTimestamp)

    // Track min/max timestamps instead of dates
    if (minTimestamp === null || startTimestamp < minTimestamp) {
      minTimestamp = startTimestamp
    }

    if (maxTimestamp === null || endTimestamp > maxTimestamp) {
      maxTimestamp = endTimestamp
    }

    // Add to the chart data
    labels.push(taskName)
    data.push({
      y: taskName,
      x: [finalStartDate, finalEndDate]
    })

    // Track deadline for gradient coloring (red only after deadline)
    if (deadlineColumn) {
      const rawDeadline = getRowValue(row, deadlineColumn.publicId, columnIdToIndex, columnStore)
      if (rawDeadline) {
        const deadlineDate = new Date(String(rawDeadline))
        if (!isNaN(deadlineDate.getTime())) {
          deadlineTimestamps.push(deadlineDate.getTime())
        } else {
          deadlineTimestamps.push(null)
        }
      } else {
        deadlineTimestamps.push(null)
      }
    } else {
      deadlineTimestamps.push(null)
    }

    const { backgroundColour } = getCellColour(
      labelColumn,
      columnValuesCount,
      row,
      taskName,
      colourSettings,
      false,
      primaryColour
    )
    colours.push(backgroundColour)
  })

  // Calculate min/max bounds with buffer
  const buffer = 7 * 24 * 60 * 60 * 1000 // 7 days in milliseconds

  // Only create Date objects at the end from the timestamps
  const minDate = minTimestamp !== null ? new Date(minTimestamp - buffer) : null
  const maxDate = maxTimestamp !== null ? new Date(maxTimestamp + buffer) : null

  return {
    labels,
    datasets: [
      {
        label: 'Duration',
        data,
        backgroundColor: colours,
        _normalColours: colours,
        _deadlines: deadlineTimestamps,
        borderRadius: 2,
        barPercentage: 0.8,
        categoryPercentage: 0.8
      }
    ],
    _minDate: minDate,
    _maxDate: maxDate
  }
}

export const buildLegend = (chartType: string, showLegend: boolean, primaryColour: string) => {
  const common = {
    position: 'bottom',
    display: showLegend,
    labels: {
      usePointStyle: true,
      boxWidth: 8,
      boxHeight: 8,
      padding: 20,
      font: { size: 12 }
    }
  }

  // Only customise legends for the two bar types
  if (chartType === 'bar' || chartType === 'horizontal-bar') {
    return {
      ...common,
      labels: {
        ...common.labels,
        // One legend entry per X-axis label
        generateLabels: (chart: any) =>
          chart.data.labels.map((lbl: string, i: number) => {
            const bg = chart.data.datasets.length > 0 ? chart.data.datasets[0].backgroundColor[i] : primaryColour
            return {
              text: lbl,
              fillStyle: bg,
              strokeStyle: bg,
              fontColor: contrastText(bg),
              index: i,
              hidden: false,
              pointStyle: 'circle'
            }
          })
      },
      // Allow users to click legend items to hide/show individual bars
      onClick: (_e: any, item: any, legend: any) => {
        const { index } = item
        const bar = legend.chart.getDatasetMeta(0).data[index]
        bar.hidden = !bar.hidden
        legend.chart.update()
      }
    }
  }

  // all other chart types keep the default legend behaviour
  return common
}
